/**
 * HttpClientContext + Provider + hook.
 *
 * Admin routes wrap their subtree with ``<HttpClientProvider client={apiClient} />``
 * and portal routes wrap with ``<HttpClientProvider client={portalClient} />``.
 * Consumers resolve the active client via ``useHttpClient()`` and pass it
 * into service calls; services branch URL construction on ``http.context.kind``.
 */

import React, { createContext, useContext, useMemo } from "react";
import type { HttpClient, HttpContext } from "./types";

const HttpClientContext = createContext<HttpClient | null>(null);

interface HttpClientProviderProps {
  client: HttpClient;
  children: React.ReactNode;
}

export const HttpClientProvider: React.FC<HttpClientProviderProps> = ({
  client,
  children,
}) => {
  // Freshen identity only when the underlying client instance changes.
  const value = useMemo(() => client, [client]);
  return (
    <HttpClientContext.Provider value={value}>
      {children}
    </HttpClientContext.Provider>
  );
};

/**
 * Resolve the active ``HttpClient`` from the nearest provider. Throws if
 * called outside an ``HttpClientProvider`` so missing wiring is caught early.
 */
export function useHttpClient(): HttpClient {
  const client = useContext(HttpClientContext);
  if (!client) {
    throw new Error(
      "useHttpClient must be used within an HttpClientProvider. " +
        "Admin routes wrap with apiClient; portal routes wrap with portalClient."
    );
  }
  return client;
}

/** Narrowed variant for services that need the typed context union. */
export function useHttpContext(): HttpContext {
  return useHttpClient().context;
}
